import { Table, type TableAction, type TableColumn } from "@/components/Table";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Icon } from "@/components/Icon";
import { ListSettings } from "@/components/ListSettings";
import type { ListPreferences } from "@/components/listPreferences";
import { ProviderFilters } from "@/pages/Providers/ProviderFilters";
import { ProviderPagination } from "@/pages/Providers/ProviderPagination";
import { useProviderContext } from "@/pages/Providers/useProviderApi";
import type { useProviders } from "@/pages/Providers/useProviders";
import type { DetailTab, Provider } from "@/pages/Providers/types";

interface ProviderTableProps {
    preferences: ListPreferences;
    onPreferences: (value: ListPreferences) => void;
    views: (configuration: React.ReactNode) => React.ReactNode;
    data: ReturnType<typeof useProviders>;
    onView: (id: number, tab?: DetailTab) => void;
    onEdit: (provider: Provider) => void;
    onStatus: (provider: Provider) => void;
}

export const ProviderTable = ({
    data,
    preferences,
    onPreferences,
    views,
    onView,
    onEdit,
    onStatus,
}: ProviderTableProps) => {
    const { t } = useProviderContext();
    const { result, loading, pagination, sorting, setPagination, setSorting } =
        data;
    const field = (
        key: keyof Provider & string,
        render?: (provider: Provider) => React.ReactNode
    ): TableColumn<Provider> => ({
        key,
        label: t(`providers.fields.${key}`),
        render,
        sortable: ["name", "id", "bc_code", "tax_id"].includes(key),
    });
    const columns: TableColumn<Provider>[] = [
        field("is_active", (row) => (
            <Badge
                variant={Number(row.is_active) === 1 ? "success" : "neutral"}
            >
                {t(
                    `providers.filters.state_${Number(row.is_active) === 1 ? "active" : "inactive"}`
                )}
            </Badge>
        )),
        field("name", (row) => (
            <Button
                variant="ghost"
                className="!justify-start !p-0 !text-left !font-semibold !text-primary dark:!text-primary-300 hover:!underline"
                onClick={() => onView(row.id)}
            >
                {row.name}
            </Button>
        )),
        field("id"),
        field("bc_code"),
        field("tax_id"),
        field("provider_type_id", (row) => row.type_name),
        field("phone"),
        field("email"),
        field("bc_present", (row) => (
            <Badge variant={row.bc_present ? "info" : "neutral"}>
                {t(
                    `providers.filters.bc_${row.bc_present ? "linked" : "unlinked"}`
                )}
            </Badge>
        )),
        field("payment_blocked", (row) =>
            Number(row.payment_blocked) ? (
                <Badge variant="warning">{t("providers.ui.yes")}</Badge>
            ) : (
                <span className="text-slate-400">{t("providers.ui.no")}</span>
            )
        ),
        field("conflict_count", (row) => (
            <Button
                size="xs"
                variant={
                    row.conflict_severity === "blocking" ? "danger" : "ghost"
                }
                onClick={() => onView(row.id, "conflicts")}
            >
                {row.conflict_count
                    ? t(`providers.ui.conflict_${row.conflict_severity}`, {
                          count: row.conflict_count,
                      })
                    : t("providers.ui.no_conflicts")}
            </Button>
        )),
    ];
    const actions: TableAction<Provider>[] = [
        {
            label: t("providers.ui.view"),
            icon: <Icon value="eye" />,
            onClick: (row) => onView(row.id),
        },
        {
            label: t("providers.ui.edit"),
            icon: <Icon value="pencil" />,
            onClick: onEdit,
        },
        {
            label: t("providers.ui.disable"),
            icon: <Icon value="ban" />,
            show: (row) => Number(row.is_active) === 1,
            onClick: onStatus,
        },
        {
            label: t("providers.ui.enable"),
            icon: <Icon value="checkCircle" />,
            show: (row) => Number(row.is_active) !== 1,
            onClick: onStatus,
        },
        {
            label: t("providers.ui.conflicts"),
            icon: <Icon value="alertTriangle" />,
            onClick: (row) => onView(row.id, "conflicts"),
        },
        {
            label: t("providers.ui.history"),
            icon: <Icon value="history" />,
            onClick: (row) => onView(row.id, "history"),
        },
    ];
    return (
        <div className="space-y-3">
            <ProviderFilters
                search={data.search}
                filters={data.filters}
                catalogs={result?.catalogs}
                columnFilters={data.columnFilters}
                onColumns={data.setColumnFilters}
                onSearch={data.updateSearch}
                onFilter={data.updateFilter}
                onClear={data.clearFilters}
                controls={views(
                    <ListSettings
                        inline
                        columns={columns}
                        value={preferences}
                        onChange={onPreferences}
                        sort={{
                            key: sorting[0].id,
                            order: sorting[0].desc ? "desc" : "asc",
                        }}
                        onSortChange={(sort) =>
                            setSorting([
                                { id: sort.key, desc: sort.order === "desc" },
                            ])
                        }
                    />
                )}
            />
            <Table<Provider>
                appearance="workspace"
                density={preferences.density}
                columns={columns.filter(
                    (column) => !preferences.hidden.includes(column.key)
                )}
                data={result?.items ?? []}
                actions={actions}
                actionsPosition="right"
                actionMenu
                paginated={false}
                searchable={false}
                isLoading={loading}
                sort={{
                    key: sorting[0].id,
                    order: sorting[0].desc ? "desc" : "asc",
                }}
                manualSorting
                onSortChange={(sort) =>
                    setSorting([{ id: sort.key, desc: sort.order === "desc" }])
                }
                emptyText={t(
                    result?.summary.total
                        ? "providers.ui.no_results"
                        : "providers.ui.empty"
                )}
                rowClassName={(row) =>
                    Number(row.is_active) === 1 ? "" : "opacity-60"
                }
            />
            <ProviderPagination
                page={pagination.pageIndex + 1}
                total={result?.total ?? 0}
                pageSize={pagination.pageSize}
                onPage={(page) =>
                    setPagination((previous) => ({
                        ...previous,
                        pageIndex: page - 1,
                    }))
                }
                onPageSize={(pageSize) =>
                    setPagination({ pageIndex: 0, pageSize })
                }
            />
        </div>
    );
};
